import { useEffect, useState, useRef } from "react";
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { useFormik } from "formik";
import { FILTER_KEYS, FILTER_LIST } from "../../../Filter/Constants/filters.constants";
import { useAppSelector, useAppDispatch } from "@App/hooks";
import { setSubscriptionData } from "@Src/app/reducers/auth/loginSlice";
import { getUserDetails } from "@Src/features/UserProfile/Services/UserProfile.sevice";
import { APIResponse, APIStatus } from "@Src/services/baseInterfaces";
import { InputText } from 'primereact/inputtext';
import { filterEvents, FILTER_EVENTS } from "@Src/utils/events/filterEvents";
import { setIabhyasTestSeries } from "../../../TestDetails/services/TestDetails.service";
import { useNavigate, useLocation } from "react-router-dom";
import { TestSereisRouteURL } from "@Constants/app-route-url.constant";
import { Checkbox } from 'primereact/checkbox';
import { Tooltip } from "react-tooltip";

export const TestSeriesAllFilter = ({ filters = {}, submitFilters, initialFilters = [], mockTestSeriesList = [], topicPriority }: any) => {
    const navigation = useNavigate();
    const location = useLocation();
    const coursePreferences = useAppSelector((state: any) => state.coursePreferences);
    const loginData = useAppSelector((state: any) => state.login);
    const isLoggedIn = loginData?.isLoggedIn;
    const filterContainerRef = useRef<HTMLDivElement>(null);
    const coursePreferencesSnapshotRef = useRef<string | null>(null);
    const [keyword, setKeyword] = useState<string>("");
    const [checked, setChecked] = useState<boolean>(false)
    const [isPracticeTest, setIsPracticeTest] = useState<boolean>(false)
    const [isKeywordSearched, setIsKeywordSearched] = useState<boolean>(false);


    useEffect(() => {
        if (location.pathname === "/testseries" || location.pathname === "/testseries/all" || location.pathname === "/full-mock-testseries") {
            setIsPracticeTest(true);
        } else {
            setIsPracticeTest(false);
        }
    }, [location])
    const dispatch = useAppDispatch();

    let insitituteSwitchButton = '';
    switch (location.pathname) {
        case TestSereisRouteURL.DIAGNOSTIC_TEST_SERIES:
            insitituteSwitchButton = "readiness check"
            break;
        case TestSereisRouteURL.TEST_SERIES:
            insitituteSwitchButton = "practice test"
            break;
        case TestSereisRouteURL.FULL_MOCK_TEST_SERIES:
            insitituteSwitchButton = "mock test"
            break;
        case TestSereisRouteURL.PREVIOUS_YEAR_MOCK_TEST_SERIES:
            insitituteSwitchButton = "previous year test"
            break;
        case `${TestSereisRouteURL.TEST_SERIES}/all`:
            insitituteSwitchButton = "practice test";
            break;
        default:
            insitituteSwitchButton = "practice test";

            break;
    }

    useEffect(() => {
        if (isLoggedIn) {
            getUserDetails().then((response: APIResponse) => {
                if (response.ResponseType === APIStatus.SUCCESS && response.data?.length > 0) {
                    dispatch(setSubscriptionData(response.data[0]));
                }
            });
        }
    }, [isLoggedIn, dispatch]);


    // Listen for navigation events to reset keyword
    useEffect(() => {
        const handleResetKeyword = () => {
            setKeyword("");
        };

        filterEvents.on(FILTER_EVENTS.RESET_KEYWORD, handleResetKeyword);

        return () => {
            filterEvents.off(FILTER_EVENTS.RESET_KEYWORD, handleResetKeyword);
        };
    }, []);

    const { handleChange, handleBlur, values, setValues, setFieldValue } = useFormik({
        initialValues: {
            [FILTER_KEYS.BOARD]: coursePreferences?.boardDetails?.[0] || null,
            [FILTER_KEYS.CLASS]: coursePreferences?.classDetails?.[0] || null,
            [FILTER_KEYS.SUBJECT]: null,
            [FILTER_KEYS.CHAPTER]: [],
            [FILTER_KEYS.TOPIC]: [],
            [FILTER_KEYS.DIFFICULTY]: null,
            [FILTER_KEYS.LANGUAGE]: null
        },
        enableReinitialize: true,
        onSubmit: () => { },
    });

    useEffect(() => {
        setInitialFilter();
    }, [initialFilters]);



    const setInitialFilter = () => {
        let values: any = {};
        initialFilters.forEach((element: any) => {
            let keys = Object.keys(element);
            keys.forEach((key: any) => {
                values[key] = element[key];
            });
        });
        setValues(values);
    };

    const fetchClassList = async (boardId: number) => {
        try {
            const { getClassList } = await import("../../../UserProfile/Services/UserProfile.sevice");
            const response: any = await getClassList({ boardId });
            if (response.ResponseType === "SUCCESS" && response.data) {
                submitFilters.updateClasses?.(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch class list:', error);
        }
    };

    const handleOnChange = (field: string, event: any) => {
        let data: any = { ...values };
        if (checked) {
            data.previousYearCheck = 1;
        }
        if (keyword && isKeywordSearched) {
            data.keyWord = keyword
        }
        data[field] = event.target.value;

        // Reset keyword input when board changes (but not from filter dropdown)
        if (field === FILTER_KEYS.BOARD) {
            // Don't reset keyword when board changes via filter dropdown
            // setKeyword("");
        }

        // Handle board -> class population for logged out users
        if (field === FILTER_KEYS.BOARD && event.target.value?.Id && !isLoggedIn) {
            fetchClassList(event.target.value.Id);
            // Clear dependents
            data[FILTER_KEYS.CLASS] = null;
            setFieldValue(FILTER_KEYS.CLASS, null);

            data[FILTER_KEYS.SUBJECT] = null;
            setFieldValue(FILTER_KEYS.SUBJECT, null);
            if (submitFilters.updateSubjects) submitFilters.updateSubjects([]);

            data[FILTER_KEYS.CHAPTER] = [];
            setFieldValue(FILTER_KEYS.CHAPTER, []);
            if (submitFilters.updateChapters) submitFilters.updateChapters([]);

            data[FILTER_KEYS.TOPIC] = [];
            setFieldValue(FILTER_KEYS.TOPIC, []);
            if (submitFilters.updateTopics) submitFilters.updateTopics([]);
        }

        // Handle class -> subject population for logged out users
        if (field === FILTER_KEYS.CLASS && event.target.value?.Id && !isLoggedIn) {
            const boardId = (values[FILTER_KEYS.BOARD] as any)?.Id || coursePreferences?.boardDetails?.[0]?.Id;
            const classId = event.target.value.Id;
            if (boardId && classId) {
                import("../../../UserProfile/Services/UserProfile.sevice").then(({ getSubjectList }) => {
                    // Note: getSubjectList typically takes { boardId, classId }
                    getSubjectList({ boardId, classId }).then((response: any) => {
                        if (response?.ResponseType === "SUCCESS" && response.data) {
                            if (submitFilters.updateSubjects) {
                                submitFilters.updateSubjects(response.data);
                            }
                        }
                    });
                });
            }
            // Clear subject selection when class changes
            data[FILTER_KEYS.SUBJECT] = null;
            setFieldValue(FILTER_KEYS.SUBJECT, null);
            // Also clear dependent filters
            data[FILTER_KEYS.CHAPTER] = [];
            setFieldValue(FILTER_KEYS.CHAPTER, []);
            if (submitFilters.updateChapters) submitFilters.updateChapters([]);

            data[FILTER_KEYS.TOPIC] = [];
            setFieldValue(FILTER_KEYS.TOPIC, []);
            if (submitFilters.updateTopics) submitFilters.updateTopics([]);
        }

        // Handle subject -> chapter population
        if (field === FILTER_KEYS.SUBJECT && event.target.value?.Id) {
            const subjectId = event.target.value.Id;
            if (subjectId === 'ALL') {
                if (submitFilters.updateChapters) submitFilters.updateChapters([]);
                if (submitFilters.updateTopics) submitFilters.updateTopics([]);
                data[FILTER_KEYS.CHAPTER] = [];
                setFieldValue(FILTER_KEYS.CHAPTER, []);
                data[FILTER_KEYS.TOPIC] = [];
                setFieldValue(FILTER_KEYS.TOPIC, []);
            } else {
                const boardId = (values[FILTER_KEYS.BOARD] as any)?.Id || coursePreferences?.boardDetails?.[0]?.Id;
                const classId = (values[FILTER_KEYS.CLASS] as any)?.Id || coursePreferences?.classDetails?.[0]?.Id;

                if (boardId && classId && subjectId) {
                    import('../../../TestDetails/services/TestDetails.service').then(({ getChapterList }) => {
                        const params = {
                            boardId: boardId,
                            classId: classId,
                            subjectId: subjectId
                        };
                        getChapterList(params).then((response: any) => {
                            if (response?.ResponseType === 'SUCCESS') {
                                if (submitFilters.updateChapters) {
                                    submitFilters.updateChapters(response.data || []);
                                }
                            }
                        }).catch((error) => {
                            console.error('Error fetching chapters:', error);
                        });
                    });
                }
                // Clear dependents
                data[FILTER_KEYS.CHAPTER] = [];
                setFieldValue(FILTER_KEYS.CHAPTER, []);

                data[FILTER_KEYS.TOPIC] = [];
                setFieldValue(FILTER_KEYS.TOPIC, []);
                if (submitFilters.updateTopics) submitFilters.updateTopics([]);
            }
        }

        // Handle chapter -> topic population
        if (field === FILTER_KEYS.CHAPTER) {
            const selectedChapters = event.value;
            if (Array.isArray(selectedChapters) && selectedChapters.length > 0) {
                const chapterIds = selectedChapters.map((c: any) => c.Id).join(',');
                const boardId = (values[FILTER_KEYS.BOARD] as any)?.Id || coursePreferences?.boardDetails?.[0]?.Id;
                const classId = (values[FILTER_KEYS.CLASS] as any)?.Id || coursePreferences?.classDetails?.[0]?.Id;
                const subjectId = (values[FILTER_KEYS.SUBJECT] as any)?.Id;
                import('../../../TestDetails/services/TestDetails.service').then(({ getTopicList }) => {
                    const params: any = {
                        chapterId: chapterIds
                    };
                    if (boardId) params.boardId = boardId;
                    if (classId) params.classId = classId;
                    if (subjectId) params.subjectId = subjectId;
                    getTopicList(params).then((response: any) => {
                        if (response?.ResponseType === 'SUCCESS') {
                            if (submitFilters.updateTopics) {
                                submitFilters.updateTopics(response.data || []);
                            }
                        }
                    }).catch((error) => {
                        console.error('Error fetching topics:', error);
                    });
                });
            } else {
                if (submitFilters.updateTopics) {
                    submitFilters.updateTopics([]);
                }
            }
            // Clear dependents
            data[FILTER_KEYS.TOPIC] = [];
            setFieldValue(FILTER_KEYS.TOPIC, []);
        }

        submitFilters(data);
    };

    const handleKeywordSearch = () => {
        const trimmedKeyword = keyword.trim();
        if (!trimmedKeyword) return;

        setIsKeywordSearched(true);
        submitFilters({
            ...values,
            keyWord: trimmedKeyword,
            previousYearCheck: checked ? 1 : 0,
        });
    };

    const handleKeywordKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleKeywordSearch();
        }
    };

    const handlePreviousYear = (e: any) => {
        setChecked(e.checked ?? false);
        const data = isKeywordSearched ? { ...values, keyWord: keyword.trim(), previousYearCheck: e.checked ? 1 : 0 } : { ...values, previousYearCheck: e.checked ? 1 : 0 };
        submitFilters(data);
    }

    const onClickClearFilter = () => {
        let initialValues: any = {
            [FILTER_KEYS.BOARD]: null,
            [FILTER_KEYS.CLASS]: null,
            [FILTER_KEYS.SUBJECT]: null,
            [FILTER_KEYS.CHAPTER]: [],
            [FILTER_KEYS.TOPIC]: [],
            [FILTER_KEYS.DIFFICULTY]: null,
            [FILTER_KEYS.LANGUAGE]: null
        };

        const keysToKeep = [FILTER_KEYS.BOARD, FILTER_KEYS.CLASS, FILTER_KEYS.LANGUAGE];

        initialFilters.forEach((element: any) => {
            let keys = Object.keys(element);
            keys.forEach((key: any) => {
                if (keysToKeep.includes(key)) {
                    initialValues[key] = element[key];
                }
            });
        });

        setKeyword("");
        setChecked(false);

        // Clear dependent lists in parent state
        if (submitFilters.updateChapters) {
            submitFilters.updateChapters([]);
        }
        if (submitFilters.updateTopics) {
            submitFilters.updateTopics([]);
        }

        // Clear navigation state so subsequent renders don't pick up analysis IDs
        navigation(location.pathname, { replace: true, state: {} });

        setValues(initialValues);
        submitFilters(initialValues);
    };

    const getMultiSelectLabel = (label: string, selectedItems: any[]) => {
        if (!selectedItems || selectedItems.length === 0) return label;

        const firstName = selectedItems[0]?.Name || '';
        const displayName = firstName.length > 4 ? firstName.substring(0, 4) + '...' : firstName;

        let result = `${label}: ${displayName}`;
        if (selectedItems.length > 1) {
            result += `, +${selectedItems.length - 1}`;
        }

        return result;
    };

    const handleTestSeriesChange = async () => {
        try {
            await setIabhyasTestSeries();
            const response = await getUserDetails();
            if (response.ResponseType === APIStatus.SUCCESS && response.data?.length > 0) {
                dispatch(setSubscriptionData(response.data[0]));
            }
        } catch (error) {
            console.log(error);
        }
    }


    // Style the filter labels after render
    useEffect(() => {
        const styleFilterLabels = () => {
            if (filterContainerRef.current) {
                const labels = filterContainerRef.current.querySelectorAll('.p-multiselect-label:not(.p-placeholder)');
                labels.forEach((label: any) => {
                    const text = label.textContent;
                    if (text && (
                        text.includes('Subject:') ||
                        text.includes('Chapter:') ||
                        text.includes('Topic:') ||
                        text.includes('Difficulty Level:')
                    )) {
                        const colonIndex = text.indexOf(':');
                        if (colonIndex > -1) {
                            const prefix = text.substring(0, colonIndex + 1);
                            const rest = text.substring(colonIndex + 1);
                            label.innerHTML = `<span class="filter-prefix">${prefix}</span>${rest}`;
                        }
                    }
                });
            }
        };

        const timer = setTimeout(styleFilterLabels, 100);
        return () => clearTimeout(timer);
    }, [values[FILTER_KEYS.SUBJECT], values[FILTER_KEYS.CHAPTER], values[FILTER_KEYS.TOPIC], values[FILTER_KEYS.DIFFICULTY]]);


    const coursePreferencesKey = JSON.stringify({
        boardDetails: coursePreferences?.boardDetails?.map(({ Id, Name }: any) => ({ Id, Name })) || [],
        classDetails: coursePreferences?.classDetails?.map(({ Id, Name }: any) => ({ Id, Name })) || [],
        educationInterestDetails: coursePreferences?.educationInterestDetails?.map(({ Id, Name }: any) => ({ Id, Name })) || [],
        subjectDetails: coursePreferences?.subjectDetails?.map(({ Id, Name }: any) => ({ Id, Name })) || [],
    });

    useEffect(() => {
        if (coursePreferencesSnapshotRef.current === null) {
            coursePreferencesSnapshotRef.current = coursePreferencesKey;
            return;
        }

        if (coursePreferencesSnapshotRef.current !== coursePreferencesKey) {
            coursePreferencesSnapshotRef.current = coursePreferencesKey;
            window.location.reload();
        }
    }, [coursePreferencesKey]);

    return (
        <section className="filter-section ia-container px-3 py-6 m-auto">
            <div className="filter-head">
                <div className="tw-flex tw-items-center tw-gap-4">
                    <div className="f-h-heading">Choose a Filter to Refine Results</div>
                    {loginData?.InstituteId && (
                        <button
                            className={loginData?.IabhyasSearch === 1 ? "ia-btn ia-btn-primary py-1 px-3 tw-text-sm" : "ia-btn ia-btn-outline-primary py-1 px-3 tw-text-sm"}
                            style={{ borderRadius: '4px' }}
                            onClick={handleTestSeriesChange}
                        >
                            {loginData?.IabhyasSearch === 1 ? `Click here to switch to institute ${topicPriority ? "recommended test" : insitituteSwitchButton} ` : `Click here to switch to iAbhyas ${topicPriority ? "recommended test" : insitituteSwitchButton} `}
                        </button>
                    )}
                </div>
            </div>
            <div className="filter-dropdown-container" ref={filterContainerRef}>
                {filters[FILTER_LIST.BOARD] && filters[FILTER_LIST.BOARD].length ? (
                    <Dropdown
                        value={values[FILTER_KEYS.BOARD]}
                        name={FILTER_KEYS.BOARD}
                        onChange={(event) => { handleChange(event); handleOnChange(FILTER_KEYS.BOARD, event); }}
                        onBlur={handleBlur}
                        options={filters[FILTER_LIST.BOARD]}
                        optionLabel="Name"
                        dataKey="Id"
                        placeholder={coursePreferences?.boardDetails?.[0]?.Name || "Examinations"}
                        className="filter-dropdown"
                        disabled={isLoggedIn}
                    />
                ) : ''}

                {filters[FILTER_LIST.CLASS] && filters[FILTER_LIST.CLASS].length ? (
                    <Dropdown
                        value={values[FILTER_KEYS.CLASS]}
                        name={FILTER_KEYS.CLASS}
                        onChange={(event) => { handleChange(event); handleOnChange(FILTER_KEYS.CLASS, event); }}
                        onBlur={handleBlur}
                        options={filters[FILTER_LIST.CLASS]}
                        optionLabel="Name"
                        dataKey="Id"
                        placeholder={coursePreferences?.classDetails?.[0]?.Name || "Class"}
                        className="filter-dropdown"
                        disabled={isLoggedIn}
                    />
                ) : ''}

                {filters[FILTER_LIST.LANGUAGE] && filters[FILTER_LIST.LANGUAGE].length ? (
                    <Dropdown
                        value={values[FILTER_KEYS.LANGUAGE]}
                        name={FILTER_KEYS.LANGUAGE}
                        onChange={(event) => { handleChange(event); handleOnChange(FILTER_KEYS.LANGUAGE, event); }}
                        onBlur={handleBlur}
                        options={filters[FILTER_LIST.LANGUAGE]}
                        optionLabel="Name"
                        dataKey="Id"
                        placeholder={loginData?.LanguageName || "Language"}
                        className="filter-dropdown"
                        disabled={isLoggedIn}
                    />
                ) : ''}

                {filters[FILTER_LIST.SUBJECT] && filters[FILTER_LIST.SUBJECT].length ? (
                    <Dropdown
                        value={values[FILTER_KEYS.SUBJECT]}
                        name={FILTER_KEYS.SUBJECT}
                        onChange={(event) => { handleChange(event); handleOnChange(FILTER_KEYS.SUBJECT, event); }}
                        onBlur={handleBlur}
                        options={filters[FILTER_LIST.SUBJECT]}
                        optionLabel="Name"
                        dataKey="Id"
                        placeholder="Subject"
                        className="filter-dropdown"
                        filter={true}
                        filterPlaceholder="Search"
                    />
                ) : ''}

                {(filters[FILTER_LIST.CHAPTER] && filters[FILTER_LIST.CHAPTER].length) || (values[FILTER_KEYS.SUBJECT] && values[FILTER_KEYS.SUBJECT].Id && values[FILTER_KEYS.SUBJECT].Id !== 'ALL') ? (
                    <MultiSelect
                        value={values[FILTER_KEYS.CHAPTER]}
                        name={FILTER_KEYS.CHAPTER}
                        onChange={(event) => { handleChange(event); handleOnChange(FILTER_KEYS.CHAPTER, event); }}
                        onBlur={handleBlur}
                        options={filters[FILTER_LIST.CHAPTER] && filters[FILTER_LIST.CHAPTER].length > 0 ? filters[FILTER_LIST.CHAPTER] : [{ Name: "No Chapter Found", Id: "no-chapter-found", disabled: true }]}
                        optionLabel="Name"
                        dataKey="Id"
                        placeholder="Chapter"
                        className="filter-dropdown"
                        filter={true}
                        filterPlaceholder="Search"
                        maxSelectedLabels={0}
                        selectedItemsLabel={getMultiSelectLabel("Chapter", values[FILTER_KEYS.CHAPTER])}
                        emptyFilterMessage="No Chapter Found"
                    />
                ) : ''}

                {(filters[FILTER_LIST.TOPIC] && filters[FILTER_LIST.TOPIC].length) || (values[FILTER_KEYS.CHAPTER] && values[FILTER_KEYS.CHAPTER].length) ? (
                    <MultiSelect
                        value={values[FILTER_KEYS.TOPIC]}
                        name={FILTER_KEYS.TOPIC}
                        onChange={(event) => { handleChange(event); handleOnChange(FILTER_KEYS.TOPIC, event); }}
                        onBlur={handleBlur}
                        options={filters[FILTER_LIST.TOPIC] && filters[FILTER_LIST.TOPIC].length > 0 ? filters[FILTER_LIST.TOPIC] : [{ Name: "No Topic Found", Id: "no-topic-found", disabled: true }]}
                        optionLabel="Name"
                        dataKey="Id"
                        placeholder="Topic"
                        className="filter-dropdown"
                        filter={true}
                        filterPlaceholder="Search"
                        maxSelectedLabels={0}
                        selectedItemsLabel={getMultiSelectLabel("Topic", values[FILTER_KEYS.TOPIC])}
                        emptyFilterMessage="No Topic Found"
                    />
                ) : ''}

                {filters[FILTER_LIST.DIFFICULTY] && filters[FILTER_LIST.DIFFICULTY].length ? (
                    <Dropdown
                        value={values[FILTER_KEYS.DIFFICULTY]}
                        name={FILTER_KEYS.DIFFICULTY}
                        onChange={(event) => { handleChange(event); handleOnChange(FILTER_KEYS.DIFFICULTY, event); }}
                        onBlur={handleBlur}
                        options={filters[FILTER_LIST.DIFFICULTY]}
                        optionLabel="Name"
                        dataKey="Id"
                        placeholder="Difficulty Level"
                        className="filter-dropdown"
                    />
                ) : ''}

                <div className="keyword-search-wrapper">
                    <span className="p-input-icon-right">
                        <InputText
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            onKeyPress={handleKeywordKeyPress}
                            placeholder="Test Series Search"
                            className="keyword-search-input"
                        />
                        <i className="pi pi-search" onClick={handleKeywordSearch} style={{ cursor: 'pointer' }} />
                    </span>
                </div>
                {
                    !topicPriority && isPracticeTest && <div className="tw-flex gap-2">

                        <div className="tw-flex gap-2 previous-check" data-tooltip-id="previousCheck" data-tooltip-content="Only Previous Year Questions">
                            <Checkbox
                                inputId="previousYear"
                                checked={checked}
                                onChange={(e) => handlePreviousYear(e)}
                            />
                            <label className="cursor-pointer" htmlFor="previousYear">PYQ</label>
                        </div>
                        <Tooltip id="previousCheck" place="top" style={{ fontSize: '12px', padding: '4px 8px' }} />
                    </div>
                }

                <a className="ia-btn ia-btn-outline-primary tw-text-lg py-1 px-2" onClick={onClickClearFilter} style={{ cursor: 'pointer' }}>
                    <svg width="16" height="17" viewBox="0 0 16 17" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_3413_3245)">
                            <path d="M3.64216 3.45501C4.85187 2.40679 6.39949 1.83078 8.00016 1.83301C11.6822 1.83301 14.6668 4.81768 14.6668 8.49968C14.6668 9.92368 14.2202 11.2437 13.4602 12.3263L11.3335 8.49968H13.3335C13.3336 7.4541 13.0263 6.43155 12.4499 5.55919C11.8735 4.68682 11.0535 4.00312 10.0916 3.5931C9.12978 3.18307 8.06864 3.06482 7.04013 3.25303C6.01163 3.44124 5.06113 3.92761 4.30683 4.65168L3.64216 3.45501ZM12.3582 13.5443C11.1485 14.5926 9.60084 15.1686 8.00016 15.1663C4.31816 15.1663 1.3335 12.1817 1.3335 8.49968C1.3335 7.07568 1.78016 5.75568 2.54016 4.67301L4.66683 8.49968H2.66683C2.66674 9.54527 2.974 10.5678 3.55039 11.4402C4.12678 12.3125 4.94687 12.9962 5.90871 13.4063C6.87054 13.8163 7.93169 13.9345 8.96019 13.7463C9.9887 13.5581 10.9392 13.0717 11.6935 12.3477L12.3582 13.5443Z" />
                        </g>
                        <defs>
                            <clipPath id="clip0_3413_3245">
                                <rect width="16" height="16" fill="white" transform="translate(0 0.5)" />
                            </clipPath>
                        </defs>
                    </svg>
                    Clear Filter
                </a>
            </div>
        </section>
    );
};